export const apiModule = `# 通用模組：Hono + KV + Auth API 骨架

> 版本：v1.0
> 適用場景：任何需要 Cloudflare Worker 後端的專案
> 輸入：資料型別定義、API 規格
> 輸出：可編譯的 Hono Worker + KV 操作 + 認證中間件

---

## 1. 技術約束

- **框架**：Hono（hono）
- **CORS**：必須全域配置，允許前端域名
- **Auth**：Bearer Token + Basic Auth 雙軌制
- **資料儲存**：Cloudflare KV（預設），可選 R2

---

## 2. Worker 入口檔案結構

\`\`\`ts
// apps/api/src/index.ts
import { Hono } from 'hono';
import { cors } from 'hono/cors';

export interface Env {
  {{KV_BINDING}}: KVNamespace;
  {{R2_BINDING}}?: R2Bucket;
  ADMIN_USERNAME: string;
  ADMIN_PASSWORD: string;
}

const app = new Hono<{ Bindings: Env }>();

// === CORS ===
app.use('*', cors({
  origin: (origin) => {
    const allowed = [
      // 本地開發
      'http://localhost:5173',
      'http://localhost:3000',
      // 生產環境（Pages 網域）
      /^https:\\/\\/.*\\.pages\\.dev$/,
      // 自訂域名
      'https://{{CUSTOM_DOMAIN}}',
    ];
    return allowed.some(a => 
      typeof a === 'string' ? a === origin : a.test(origin)
    ) ? origin : '';
  },
  allowMethods: ['GET', 'POST', 'PUT', 'DELETE', 'OPTIONS'],
  allowHeaders: ['Content-Type', 'Authorization'],
  credentials: true,
}));

// === Auth 驗證 ===
function verifyAdmin(auth: string | undefined, env: Env): boolean {
  if (!auth) return false;
  const token = auth.replace('Bearer ', '').trim();
  const expectedPassword = env.ADMIN_PASSWORD || '{{FALLBACK_PASSWORD}}';
  if (token === '{{ADMIN_TOKEN}}') return true;
  if (auth.startsWith('Basic ')) {
    const decoded = atob(auth.slice(6));
    return decoded === \`\${env.ADMIN_USERNAME}:\${expectedPassword}\`;
  }
  return false;
}

// === Middleware ===
const adminAuth = async (c, next) => {
  const auth = c.req.header('Authorization');
  if (!verifyAdmin(auth, c.env)) {
    return c.json({ error: 'Unauthorized' }, 401);
  }
  await next();
};

// === Health Check ===
app.get('/', (c) => c.json({ status: 'ok', version: '1.0.0' }));

// === Data API ===
app.get('/api/data', async (c) => { /* ... */ });
app.post('/api/data', adminAuth, async (c) => { /* ... */ });
app.post('/api/reset', adminAuth, async (c) => { /* ... */ });

// === Auth ===
app.post('/api/admin/login', async (c) => { /* ... */ });

// === Upload / Media ===
app.post('/api/upload', adminAuth, async (c) => { /* ... */ });
app.get('/api/media/:id', async (c) => { /* ... */ });

// === Analytics ===
app.post('/api/analytics/event', async (c) => { /* ... */ });
app.get('/api/analytics', adminAuth, async (c) => { /* ... */ });

export default app;
\`\`\`

---

## 3. Deep Merge 規則（通用實作）

\`\`\`ts
/**
 * 深度合併物件。陣列與基本型別由 source 覆蓋 target。
 */
function deepMerge<T>(target: T, source: Partial<T>): T {
  if (source === null || source === undefined) return target;
  if (typeof target !== 'object' || typeof source !== 'object') return source as T;
  if (Array.isArray(target) || Array.isArray(source)) return source as T;

  const result = { ...target } as Record<string, any>;
  for (const key of Object.keys(source)) {
    const val = (source as Record<string, any>)[key];
    if (val !== null && typeof val === 'object' && !Array.isArray(val) && key in result) {
      result[key] = deepMerge(result[key], val);
    } else {
      result[key] = val;
    }
  }
  return result as T;
}
\`\`\`

---

## 4. KV 操作封裝

\`\`\`ts
// 通用 KV 操作
const DATA_KEY = '{{DATA_KEY}}';           // 主資料 key
const HISTORY_PREFIX = '{{HISTORY_PREFIX}}'; // 歷史記錄前綴

async function getData(kv: KVNamespace, defaultData: any): Promise<any> {
  const stored = await kv.get(DATA_KEY, 'json');
  return stored ? deepMerge(defaultData, stored) : defaultData;
}

async function saveData(kv: KVNamespace, data: any): Promise<void> {
  data.lastUpdated = new Date().toISOString();
  await kv.put(DATA_KEY, JSON.stringify(data));
  // 保留歷史 30 天
  const historyKey = \`\${HISTORY_PREFIX}_\${data.lastUpdated}\`;
  await kv.put(historyKey, JSON.stringify(data), { expirationTtl: 86400 * 30 });
}
\`\`\`

---

## 5. Auth 登入端點

\`\`\`ts
app.post('/api/admin/login', async (c) => {
  const { username, password } = await c.req.json();
  const env = c.env;
  const expectedPassword = env.ADMIN_PASSWORD || '{{FALLBACK_PASSWORD}}';
  
  if (username === env.ADMIN_USERNAME && password === expectedPassword) {
    return c.json({
      token: '{{ADMIN_TOKEN}}',
      username: env.ADMIN_USERNAME,
    });
  }
  return c.json({ error: 'Invalid credentials' }, 401);
});
\`\`\`

---

## 6. 媒體上傳規則

\`\`\`ts
app.post('/api/upload', adminAuth, async (c) => {
  const formData = await c.req.formData();
  const file = formData.get('file') as File;
  if (!file) return c.json({ error: 'No file' }, 400);

  const MAX_SIZE = 1024 * 1024 * 1024; // 1GB
  if (file.size > MAX_SIZE) {
    return c.json({ error: 'File too large', max: '1GB' }, 413);
  }

  const id = crypto.randomUUID();
  const buffer = await file.arrayBuffer();
  const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));

  const mediaData = {
    id,
    name: file.name,
    mimeType: file.type,
    size: file.size,
    data: base64,
    createdAt: new Date().toISOString(),
  };

  await c.env.{{KV_BINDING}}.put(\`media_\${id}\`, JSON.stringify(mediaData));
  return c.json({ id, url: \`/api/media/\${id}\` });
});
\`\`\`

---

## 7. Analytics 事件收集

\`\`\`ts
app.post('/api/analytics/event', async (c) => {
  const event = await c.req.json();
  const bucketKey = \`analytics_events_\${new Date().toISOString().split('T')[0]}\`;
  
  const existing = await c.env.{{KV_BINDING}}.get(bucketKey, 'json') || [];
  existing.push({
    id: crypto.randomUUID(),
    ...event,
    timestamp: new Date().toISOString(),
  });
  
  await c.env.{{KV_BINDING}}.put(bucketKey, JSON.stringify(existing));
  return c.json({ ok: true });
});
\`\`\`

---

## 8. 通用陷阱

1. **CORS 必須在最前面**：\`app.use('*', cors(...))\` 要放在所有路由之前
2. **KV 讀寫是非同步的**：所有 KV 操作前必須加 \`await\`
3. **TypeScript 型別**：\`Env\` interface 必須與 \`wrangler.toml\` 的 binding 名稱一致
4. **Base64 編碼**：\`btoa\` 在 Worker 環境可用，但對大檔案要考慮 R2
5. **歷史記錄的 expirationTtl**：KV 的 TTL 以秒為單位，30 天 = 2592000
`;
